import './Contact.scss';

import * as Yup from 'yup';

import { Alert, Col, Form, Row } from 'react-bootstrap';
import { GOOGLE, LANGUAGES } from '../../utils/constants';
import { useRef, useState } from 'react';

import { Button } from 'react-bootstrap';
import { FiAlertTriangle } from 'react-icons/fi';
import { Formik } from 'formik';
import LazyLoad from 'react-lazyload';
import LoaderComponent from '../Loader/LoaderComponents';
import ReCAPTCHA from 'react-google-recaptcha';
import messages from './messages';
import { postMail } from '../../services/api';
import { selectLanguageSettings } from '../../ducks/settings';
import siteMessages from '../../pages/root/siteMessages';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

interface OwnProps {
  setFormModified(value: boolean): void;
}

const Contact = (props: OwnProps) => {
  const languageId = useSelector(selectLanguageSettings);
  const intl = useIntl();
  const [sendState, setSendState] = useState<boolean>();
  const [recaptchaError, setRecaptchaError] = useState('');
  const scrollBehavior = { behavior: 'smooth', block: 'center' } as any;

  const recaptcha = useRef<any>();

  const schema = Yup.object().shape({
    firstname: Yup.string()
      .min(
        1,
        intl.formatMessage({ ...siteMessages.FIELD_TOO_SHORT }, { len: 1 })
      )
      .max(
        50,
        intl.formatMessage({ ...siteMessages.FIELD_TOO_LONG }, { len: 50 })
      )
      .required(intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })),
    lastname: Yup.string()
      .min(
        1,
        intl.formatMessage({ ...siteMessages.FIELD_TOO_SHORT }, { len: 1 })
      )
      .max(
        50,
        intl.formatMessage({ ...siteMessages.FIELD_TOO_LONG }, { len: 50 })
      )
      .required(intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })),
    email: Yup.string()
      .email(intl.formatMessage({ ...siteMessages.FIELD_INVALID_EMAIL }))
      .max(
        256,
        intl.formatMessage({ ...siteMessages.FIELD_TOO_LONG }, { len: 256 })
      )
      .required(intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })),
    subject: Yup.string()
      .min(
        3,
        intl.formatMessage({ ...siteMessages.FIELD_TOO_SHORT }, { len: 3 })
      )
      .max(
        50,
        intl.formatMessage({ ...siteMessages.FIELD_TOO_LONG }, { len: 50 })
      )
      .required(intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })),
    message: Yup.string()
      .min(
        10,
        intl.formatMessage({ ...siteMessages.FIELD_TOO_SHORT }, { len: 10 })
      )
      .max(
        1000,
        intl.formatMessage({ ...siteMessages.FIELD_TOO_LONG }, { len: 1000 })
      )
      .required(intl.formatMessage({ ...siteMessages.FIELD_REQUIRED }))
  });

  const onSubmit = async (values: any, { setSubmitting, resetForm }: any) => {
    setSubmitting(true);
    setSendState(undefined);

    const googleRecaptcha = recaptcha.current.props.grecaptcha;
    const recaptchaResponse = googleRecaptcha.getResponse();
    if (!recaptchaResponse) {
      setRecaptchaError(intl.formatMessage({ ...messages.RECAPTCHA_MISSING }));
      return;
    }
    setRecaptchaError('');
    await postMail(
      {
        name: `${values.firstname} ${values.lastname}`,
        mailFrom: values.email,
        subject: values.subject,
        body: values.message
      },
      recaptchaResponse
    ).then((res: any) => {
      if (res) resetForm();
      setSubmitting(false);
      setSendState(res);
      document.querySelector('.contactus')?.scrollIntoView(scrollBehavior);
      recaptcha.current.reset();
    });
  };

  return (
    <div className="contactus">
      <h2 className="d-flex justify-content-center mb-3">
        {intl.formatMessage({ ...messages.title })}
      </h2>
      <p
        className="contactus-description"
        dangerouslySetInnerHTML={{
          __html: intl.formatMessage({ ...messages.description })
        }}
      />
      {sendState !== undefined && (
        <Alert variant={sendState ? 'success' : 'danger'}>
          {sendState
            ? intl.formatMessage({ ...messages.confirmationSuccess })
            : intl.formatMessage({ ...messages.confirmationFail })}
        </Alert>
      )}
      <p className="contactus-alert">
        {intl.formatMessage({ ...siteMessages.required })}
      </p>
      <Formik
        initialValues={{
          firstname: '',
          lastname: '',
          email: '',
          subject: '',
          message: ''
        }}
        validationSchema={schema}
        onSubmit={onSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit,
          isSubmitting
        }) => (
          <Form
            noValidate
            onSubmit={handleSubmit}
            onChange={() => props.setFormModified(true)}
          >
            <Row>
              <Col md>
                <Form.Group controlId="contactFirstname">
                  <Form.Label className="input">
                    {intl.formatMessage({ ...messages.firstname })}
                  </Form.Label>
                  <Form.Control
                    name="firstname"
                    placeholder={intl.formatMessage({
                      ...messages.firstnamePlaceholder
                    })}
                    onChange={handleChange}
                    value={values.firstname}
                    required
                    isInvalid={touched.firstname && !!errors.firstname}
                  />
                  <Form.Control.Feedback type="invalid">
                    <FiAlertTriangle className="mb-1 me-1" />
                    {errors.firstname}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md>
                <Form.Group controlId="contactLastname">
                  <Form.Label className="input">
                    {intl.formatMessage({ ...messages.lastname })}
                  </Form.Label>
                  <Form.Control
                    name="lastname"
                    placeholder={intl.formatMessage({
                      ...messages.lastnamePlaceholder
                    })}
                    onChange={handleChange}
                    value={values.lastname}
                    required
                    isInvalid={touched.lastname && !!errors.lastname}
                  />
                  <Form.Control.Feedback type="invalid">
                    <FiAlertTriangle className="mb-1 me-1" />
                    {errors.lastname}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group controlId="contactEmail" className="contactus-row">
              <Form.Label className="input">
                {intl.formatMessage({ ...messages.email })}
              </Form.Label>
              <Form.Control
                type="email"
                name="email"
                placeholder={intl.formatMessage({
                  ...messages.emailPlaceholder
                })}
                onChange={handleChange}
                value={values.email}
                required
                isInvalid={touched.email && !!errors.email}
              />
              <Form.Control.Feedback type="invalid">
                <FiAlertTriangle className="mb-1 me-1" />
                {errors.email}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="contactSubject" className="contactus-row">
              <Form.Label className="input">
                {intl.formatMessage({ ...messages.subject })}
              </Form.Label>
              <Form.Control
                name="subject"
                placeholder={intl.formatMessage({
                  ...messages.subjectPlaceholder
                })}
                onChange={handleChange}
                value={values.subject}
                required
                isInvalid={touched.subject && !!errors.subject}
              />
              <Form.Control.Feedback type="invalid">
                <FiAlertTriangle className="mb-1 me-1" />
                {errors.subject}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="contactMessage" className="contactus-row">
              <Form.Label className="input">
                {intl.formatMessage({ ...messages.message })}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                name="message"
                placeholder={intl.formatMessage({
                  ...messages.messagePlaceholder
                })}
                onChange={handleChange}
                value={values.message}
                required
                isInvalid={touched.message && !!errors.message}
              />
              <Form.Control.Feedback type="invalid">
                <FiAlertTriangle className="mb-1 me-1" />
                {errors.message}
              </Form.Control.Feedback>
              <LazyLoad className="mt-3" height={100} once offset={100}>
                <ReCAPTCHA
                  ref={recaptcha}
                  sitekey={GOOGLE.captchaId}
                  hl={
                    LANGUAGES.find((l) => l.id === languageId)?.locale ||
                    LANGUAGES[0].locale!
                  }
                  onChange={() => setRecaptchaError('')}
                />
                {recaptchaError && (
                  <div className="recaptcha-error">{recaptchaError}</div>
                )}
              </LazyLoad>
            </Form.Group>
            <div className="wrapper-btn">
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                <LoaderComponent isLoading={isSubmitting} className="lds-small">
                  {intl.formatMessage({ ...messages.send })}
                </LoaderComponent>
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default Contact;
